import { RoyaltiesGridData } from 'src/hooks/analytics';

import { VENDOR_COLORS } from './constants';
import {
    ChartDataPoint,
    VendorCategory,
    aggregateRoyaltiesByMajorLabels,
    filterChartDataByVendors,
    validateChartData
} from './dataTransformation';

export interface RoyaltiesChartDataInput {
    data: RoyaltiesGridData[];
    filteredTerritories?: string[];
    filteredVendors?: string[];
}

interface ChartDataOutput {
    barChartData: ChartDataPoint[];
    lineChartData: ChartDataPoint[];
    availableVendors: VendorCategory[];
}

/**
 * Generates chart data for Royalties tab with regular stacked bar charts
 */
export function generateRoyaltiesChartData(input: RoyaltiesChartDataInput): ChartDataOutput {
    const { data, filteredTerritories, filteredVendors } = input;

    try {
        // Return empty data if no input data
        if (!data || data.length === 0) {
            return {
                barChartData: [],
                lineChartData: [],
                availableVendors: []
            };
        }

        // Data validation is now handled in aggregateRoyaltiesByMajorLabels

        // Aggregate data by major labels and Indies (includes validation)
        const aggregatedData = aggregateRoyaltiesByMajorLabels(data, filteredTerritories, filteredVendors);

        if (aggregatedData.length === 0) {
            return {
                barChartData: [],
                lineChartData: [],
                availableVendors: []
            };
        }

        // Convert aggregated data to chart data points
        const chartData: ChartDataPoint[] = aggregatedData.map((item) => ({
            month: item.month,
            UMG: item.UMG,
            SONY: item.SONY,
            WMG: item.WMG,
            Indies: item.Indies
        }));

        // Validate chart data for progressive loading
        const validation = validateChartData(chartData);
        if (!validation.isValid) {
            console.warn('Royalties chart data validation issues:', validation.issues);
        }

        // Get available vendors from the chart data
        const { availableVendors } = filterChartDataByVendors(chartData, []);

        // For royalties, both bar and line charts use the same data (no normalization needed)
        return {
            barChartData: chartData,
            lineChartData: chartData,
            availableVendors
        };
    } catch (error) {
        console.error('Error generating royalties chart data:', error);
        return {
            barChartData: [],
            lineChartData: [],
            availableVendors: []
        };
    }
}

/**
 * Creates chart series configuration for royalties bar chart
 */
export function createRoyaltiesBarSeries(availableVendors: VendorCategory[]) {
    return availableVendors.map((vendor) => ({
        type: 'bar' as const,
        xKey: 'month',
        yKey: vendor,
        yName: vendor,
        stacked: true,
        fill: VENDOR_COLORS[vendor],
        tooltip: {
            renderer: (params: any) => {
                const { datum, yKey } = params;
                const value = datum[yKey];
                const formattedValue =
                    value >= 1000000 ? `$${(value / 1000000).toFixed(1)}M` : `$${value.toLocaleString()}`;

                return {
                    title: yKey,
                    content: `Royalties: ${formattedValue}`
                };
            }
        }
    }));
}

/**
 * Creates chart series configuration for royalties line chart
 */
export function createRoyaltiesLineSeries(availableVendors: VendorCategory[]) {
    return availableVendors.map((vendor) => ({
        type: 'line' as const,
        xKey: 'month',
        yKey: vendor,
        yName: vendor,
        marker: {
            enabled: true,
            size: 6
        },
        tooltip: {
            renderer: (params: any) => {
                const { datum, yKey } = params;
                const value = datum[yKey];
                const formattedValue =
                    value >= 1000000 ? `$${(value / 1000000).toFixed(1)}M` : `$${value.toLocaleString()}`;

                return {
                    title: yKey,
                    content: `Royalties: ${formattedValue}`
                };
            }
        }
    }));
}
